import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

/**
 * Sections that require a tenant_id prefix in the URL.
 * Routes under these sections are structured as /{tenant_id}/{section}/...
 */
const TENANT_SCOPED_SECTIONS = [
    "layer",
    "ripening-room",
    "packing-list",
    "signals",
    "mailbox",
    "admin",
    "sourcing-list",
];

/**
 * Builds a tenant-scoped absolute path from scratch.
 * Example: toTenantPath("bakker-id", "layer", "tab/operation/intake")
 *          → "/bakker-id/layer/tab/operation/intake"
 */
export function toTenantPath(tenantId: string, section: string, rest?: string): string {
    const restPart = rest ? `/${rest}` : "";
    return `/${tenantId}/${section}${restPart}`;
}

/**
 * Resolves an existing absolute path to include the tenant prefix, if the
 * path starts with one of the tenant-scoped sections.
 *
 * Already-prefixed paths (starting with /{tenantId}/) are returned unchanged.
 *
 * Example: resolveTenantPath("/layer/tab/operation/intake", "bakker-id")
 *          → "/bakker-id/layer/tab/operation/intake"
 *
 * Example: resolveTenantPath("/settings", "bakker-id")
 *          → "/settings"  (not tenant-scoped)
 */
export function resolveTenantPath(path: string, tenantId?: string): string {
    if (!tenantId || !path) return path;

    const section = TENANT_SCOPED_SECTIONS.find(
        (s) => path === `/${s}` || path.startsWith(`/${s}/`) || path.startsWith(`/${s}?`)
    );

    if (!section) return path;

    // Already prefixed — don't double-prefix
    if (path.startsWith(`/${tenantId}/`)) return path;

    return `/${tenantId}${path}`;
}

/**
 * Hook that returns a function to build tenant-scoped paths using the
 * current user's tenant_id from Redux.
 *
 * Usage:
 *   const toPath = useTenantPath();
 *   navigate(toPath("layer", `tab/${tabCategory}/${tab.value}`));
 */
export function useTenantPath() {
    const tenantId = useSelector((state: any) => state.auth.user?.tenant?.tenant_id);
    return (section: string, rest?: string) => toTenantPath(tenantId, section, rest);
}

/**
 * Drop-in replacement for useNavigate() that automatically prefixes
 * tenant-scoped paths with the current user's tenant_id.
 *
 * Usage:
 *   const navigate = useTenantNavigate();
 *   navigate("/layer/tab/operation/intake");  // → "/{tenantId}/layer/tab/..."
 */
export function useTenantNavigate() {
    const navigate = useNavigate();
    const tenantId = useSelector((state: any) => state.auth.user?.tenant?.tenant_id);

    return (path: string | number, options?: any) => {
        if (typeof path === "number") {
            return navigate(path);
        }
        return navigate(resolveTenantPath(path, tenantId), options);
    };
}
